/**
 * LLMValidationPanel Component
 * ORCA360 - Tier 4 LLM Evidence Validation
 *
 * Reusable panel that allows users to run LLM-based (Tier 4) validation
 * on a single assessment's evidence. Works for both pilot and instructor
 * assessments.
 *
 * Feature toggle:
 * - Defaults from VITE_ENABLE_LLM_EVIDENCE_VALIDATION env var
 * - User can override via the toggle in the UI
 * - Preference persisted in localStorage under 'orca_llm_validation_enabled'
 *
 * Props:
 *   compact = true  → toggle + button only (for EvidenceEditModal)
 *   compact = false → toggle + button + full per-item results table
 *
 * Date: 2026-03-04
 */

import { useState, useEffect } from 'react';
import { useLLMEvidenceValidation } from '../hooks/useLLMEvidenceValidation';

const STORAGE_LLM_EVIDENCE_VALIDATION_KEY = 'orca_llm_evidence_validation_enabled';
const LLM_EVIDENCE_VALIDATION_DEFAULT = import.meta.env.VITE_ENABLE_LLM_EVIDENCE_VALIDATION === 'true';
const showToggle = false;

export interface Evidence {
  quote: string;
  timestamp: string;
  validation_status: string;
  confidence?: number;
  type?: string;
  observation?: string;
  // Tier 4 fields
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  llm_reasoning?: string;
  reclassified_to_competency?: string;
}

export interface LLMValidationPanelProps {
  sessionId: string;
  assessmentId: string;
  assessmentType: 'pilot' | 'instructor';
  evidence: Evidence[];
  onComplete: (updated: Evidence[]) => void;
  compact?: boolean;
}

function readStoredPreference(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_LLM_EVIDENCE_VALIDATION_KEY);
    if (stored === null) return LLM_EVIDENCE_VALIDATION_DEFAULT;
    return stored === 'true';
  } catch {
    return LLM_EVIDENCE_VALIDATION_DEFAULT;
  }
}

function writeStoredPreference(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_LLM_EVIDENCE_VALIDATION_KEY, String(value));
  } catch {
    // Ignore storage errors
  }
}

export function LLMValidationPanel({
  sessionId,
  assessmentId,
  assessmentType,
  evidence,
  onComplete,
  compact = false,
}: LLMValidationPanelProps) {
  const [enabled, setEnabled] = useState<boolean>(readStoredPreference);
  const [localEvidence, setLocalEvidence] = useState<Evidence[]>(evidence);
  const { validate, isValidating, error, stats } = useLLMEvidenceValidation();

  // Keep local evidence in sync when prop changes
  useEffect(() => {
    setLocalEvidence(evidence);
  }, [evidence]);

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    writeStoredPreference(next);
  };

  const handleValidate = async () => {
    try {
      const updated = await validate({
        sessionId,
        assessmentId,
        assessmentType,
        evidence: localEvidence,
      }) as Evidence[];
      setLocalEvidence(updated);
      onComplete(updated);
    } catch {
      // error state is handled by the hook
    }
  };

  const toggle = (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        role="switch"
        aria-checked={enabled}
        onClick={handleToggle}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          enabled ? 'bg-orca-elevate' : 'bg-steel/30'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-4.5' : 'translate-x-0.5'
          }`}
        />
      </div>
      <span className="text-xs font-medium text-steel">AI Validation (Tier 4)</span>
    </label>
  );

  const button = enabled ? (
    <button
      onClick={handleValidate}
      disabled={isValidating}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 disabled:opacity-60 disabled:cursor-not-allowed rounded-md transition-colors"
    >
      {isValidating ? (
        <>
          <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Validating…
        </>
      ) : (
        <>
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Validate with AI
        </>
      )}
    </button>
  ) : null;

  // ── Compact mode: toggle + button only ──────────────────────────────────────
  if (compact) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {showToggle && (
          {toggle}
        )}
        {button}
        {error && (
          <span className="text-xs text-red-600">⚠ {error}</span>
        )}
      </div>
    );
  }

  // ── Full mode: toggle + button ──────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {showToggle && toggle}
          {button}
        </div>
        {stats && (
          <span className="text-xs text-steel">
            {stats.accepted} accepted · {stats.rejected} rejected · {stats.reclassified} reclassified
          </span>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">⚠ {error}</p>
      )}
    </div>
  );
}
